import { lazy as reactLazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import { SiteProvider } from "./lib/site";
import { useTheme } from "./lib/theme";

const retry = (load) => () => load().then((m) => { sessionStorage.removeItem("eps_chunk_reload"); return m; }).catch((e) => {
  if (!sessionStorage.getItem("eps_chunk_reload")) {
    sessionStorage.setItem("eps_chunk_reload", "1");
    window.location.reload();
    return new Promise(() => {});
  }
  throw e;
});
const lazy = (load) => reactLazy(retry(load));
const mod = (name) => lazy(() => import("./pages/Modules").then((m) => ({ default: m[name] })));
const MaintenancePage = mod("MaintenancePage");
const FirePage = mod("FirePage");
const MachinesPage = mod("MachinesPage");
const WelfarePage = mod("WelfarePage");
const FirstAidPage = mod("FirstAidPage");
const LaddersPage = mod("LaddersPage");
const LegionellaPage = mod("LegionellaPage");
const AsrsPage = mod("AsrsPage");
const SiteSetup = lazy(() => import("./pages/SiteSetup"));
const Audits = lazy(() => import("./pages/Audits"));
const PathwayRunner = lazy(() => import("./pages/PathwayRunner"));
const PathwayBuilder = lazy(() => import("./pages/PathwayBuilder"));
const QuestionLibrary = lazy(() => import("./pages/QuestionLibrary"));
const AreaLabels = lazy(() => import("./pages/AreaLabels"));
const AssetLabels = lazy(() => import("./pages/AssetLabels"));
const MyChecks = lazy(() => import("./pages/MyChecks"));
const Training = lazy(() => import("./pages/Training"));
const TrainingSession = lazy(() => import("./pages/TrainingSession"));
const InspectionRunner = lazy(() => import("./pages/InspectionRunner"));
const Coshh = lazy(() => import("./pages/Coshh"));
const Incidents = lazy(() => import("./pages/Incidents"));
const Reports = lazy(() => import("./pages/Reports"));
const Users = lazy(() => import("./pages/Users"));
const Scan = lazy(() => import("./pages/Scan"));
const Sites = lazy(() => import("./pages/Sites"));
const GroupDashboard = lazy(() => import("./pages/GroupDashboard"));
const SdsPublic = lazy(() => import("./pages/SdsPublic"));
const CoursePlayer = lazy(() => import("./pages/CoursePlayer"));

export const PageLoading = () => <div className="flex min-h-[40vh] items-center justify-center text-slate-500" data-testid="page-loading">Loading...</div>;

const ThemedToaster = () => <Toaster theme={useTheme().theme} position="top-center" richColors />;

const Protected = () => {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center text-slate-500" data-testid="auth-loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout />;
};

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/service-worker.js").catch(() => {});
  }, []);
  return (
    <AuthProvider>
      <SiteProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sds/:token" element={<SdsPublic />} />
          <Route element={<Protected />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/fire" element={<FirePage />} />
            <Route path="/coshh" element={<Coshh />} />
            <Route path="/puwer" element={<MachinesPage />} />
            <Route path="/welfare" element={<WelfarePage />} />
            <Route path="/first-aid" element={<FirstAidPage />} />
            <Route path="/ladders" element={<LaddersPage />} />
            <Route path="/incidents" element={<Incidents />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/users" element={<Users />} />
            <Route path="/scan" element={<Scan />} />
            <Route path="/sites" element={<Sites />} />
            <Route path="/group" element={<GroupDashboard />} />
            <Route path="/legionella" element={<LegionellaPage />} />
            <Route path="/asrs" element={<AsrsPage />} />
            <Route path="/site-setup" element={<SiteSetup />} />
            <Route path="/audits" element={<Audits />} />
            <Route path="/pathway/:id" element={<PathwayRunner />} />
            <Route path="/pathway-builder" element={<PathwayBuilder />} />
            <Route path="/question-library" element={<QuestionLibrary />} />
            <Route path="/pathway-builder/:id" element={<PathwayBuilder />} />
            <Route path="/area-labels" element={<AreaLabels />} />
            <Route path="/asset-labels" element={<AssetLabels />} />
            <Route path="/my-checks" element={<MyChecks />} />
            <Route path="/training" element={<Training />} />
            <Route path="/training/course/:cid" element={<CoursePlayer />} />
            <Route path="/training/:id" element={<TrainingSession />} />
            <Route path="/inspect/:template" element={<InspectionRunner />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </SiteProvider>
      <ThemedToaster />
    </AuthProvider>
  );
}

export default App;
